import type { StoryboardSceneDraft } from './types'

interface StoryboardSceneDraftOptions {
  nextClientId: () => string
  resolveDuration: (scene?: Record<string, any> | null) => number
  buildImagePrompt: (scene: Record<string, any>) => string
  buildVideoPrompt: (scene: Record<string, any>) => string
}

export function cloneScene<T>(value: T): T {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value
  }
  return JSON.parse(JSON.stringify(value)) as T
}

export function buildInsertedStoryboardScene(
  referenceScene: Record<string, any> | undefined,
  options: StoryboardSceneDraftOptions,
) {
  return {
    _clientId: options.nextClientId(),
    scene_number: 0,
    location: '',
    description: '',
    action: '',
    dialogue_summary: '',
    shot_type: referenceScene?.shot_type || '',
    camera_angle: referenceScene?.camera_angle || '',
    camera_movement: referenceScene?.camera_movement || '',
    time_of_day: referenceScene?.time_of_day || '',
    mood: referenceScene?.mood || '',
    visual_cues: Array.isArray(referenceScene?.visual_cues) ? cloneScene(referenceScene.visual_cues) : [],
    characters: Array.isArray(referenceScene?.characters) ? cloneScene(referenceScene.characters) : [],
    duration: options.resolveDuration(referenceScene),
    official_character_asset_ids: Array.isArray(referenceScene?.official_character_asset_ids) ? cloneScene(referenceScene.official_character_asset_ids) : [],
    official_scene_asset_id: String(referenceScene?.official_scene_asset_id || ''),
    official_prop_asset_ids: Array.isArray(referenceScene?.official_prop_asset_ids) ? cloneScene(referenceScene.official_prop_asset_ids) : [],
  }
}

export function normalizeStoryboardSceneDraft(
  scene: Record<string, any>,
  index: number,
  options: StoryboardSceneDraftOptions,
): StoryboardSceneDraft {
  const backgroundPrompt = String(scene?.background_prompt || '').trim() || options.buildImagePrompt(scene)
  const videoPrompt = String(scene?.video_prompt || '').trim() || options.buildVideoPrompt(scene)
  const description = String(scene?.description || scene?.scene_description || scene?.storyboard_description || '')
  const hasExplicitAction = ['action', 'action_description', 'visual_description', 'shot_description']
    .some((key) => Object.prototype.hasOwnProperty.call(scene || {}, key))
  const legacyActionFallback = !hasExplicitAction ? backgroundPrompt : ''
  const action = String(scene?.action || scene?.action_description || scene?.visual_description || scene?.shot_description || legacyActionFallback || '')
  const dialogueSummary = String(scene?.dialogue_summary || scene?.dialogue || scene?.dialogue_text || '')

  return {
    ...cloneScene(scene),
    _clientId: String(scene?._clientId || options.nextClientId()),
    scene_number: index + 1,
    location: String(scene?.location || ''),
    description,
    action,
    dialogue_summary: dialogueSummary,
    background_prompt: backgroundPrompt,
    duration: options.resolveDuration(scene),
    shot_type: String(scene?.shot_type || ''),
    camera_angle: String(scene?.camera_angle || ''),
    camera_movement: String(scene?.camera_movement || ''),
    official_character_asset_ids: Array.isArray(scene?.official_character_asset_ids)
      ? scene.official_character_asset_ids.map((item: unknown) => String(item || '').trim()).filter(Boolean)
      : [],
    official_scene_asset_id: String(scene?.official_scene_asset_id || '').trim(),
    official_prop_asset_ids: Array.isArray(scene?.official_prop_asset_ids)
      ? scene.official_prop_asset_ids.map((item: unknown) => String(item || '').trim()).filter(Boolean)
      : [],
    video_prompt: videoPrompt,
  }
}

export function mergeStoryboardSceneServerFields(localScene: StoryboardSceneDraft | undefined, serverScene: StoryboardSceneDraft) {
  if (!localScene) {
    return serverScene
  }

  return {
    ...serverScene,
    ...localScene,
    scene_number: serverScene.scene_number,
    background_image_url: serverScene.background_image_url,
    background_generation: cloneScene(serverScene.background_generation),
    selected_image_source: serverScene.selected_image_source,
    reference_image_urls: cloneScene(serverScene.reference_image_urls),
    character_images_urls: cloneScene(serverScene.character_images_urls),
  } as StoryboardSceneDraft
}